import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import Button from '../Button';
import useSwipeToClose from '../hooks/useSwipeToClose';
import './InfoTooltip.css';
import { drawerDurationMs } from '../utils/cssDuration';

/**
 * InfoTooltip — Generic DS popup / bottom-sheet
 *
 * Desktop: centered modal popup
 * Mobile:  bottom-sheet drawer with swipe-to-dismiss
 *
 * Renders `children` as content. Fully generic — no hardcoded layout inside.
 *
 * Props:
 *  - isOpen     — controlled open state
 *  - onClose    — close callback
 *  - children   — any content to render inside the popup/drawer body
 *  - ariaLabel  — accessible label for the dialog
 */
const InfoTooltip = ({ isOpen = false, onClose, children, ariaLabel = '' }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [isEntered, setIsEntered] = useState(false);
  const overlayRef = useRef(null);
  const enteredTimer = useRef(null);
  const closeTimer = useRef(null);
  const scrollYRef = useRef(0);

  /* ── Open / close lifecycle ── */
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

      if (isDesktop) {
        scrollYRef.current = window.scrollY;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollYRef.current}px`;
        document.body.style.width = '100%';
        if (scrollbarWidth > 0) {
          document.body.style.paddingRight = `${scrollbarWidth}px`;
        }
      }

      enteredTimer.current = setTimeout(() => setIsEntered(true), drawerDurationMs() + 50);
    }
    return () => {
      if (enteredTimer.current) clearTimeout(enteredTimer.current);
      if (closeTimer.current) clearTimeout(closeTimer.current);
      if (document.body.style.position === 'fixed') {
        const savedY = scrollYRef.current;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.paddingRight = '';
        window.scrollTo(0, savedY);
      }
    };
  }, [isOpen]);

  const unlockScroll = useCallback(() => {
    if (document.body.style.position === 'fixed') {
      const savedY = scrollYRef.current;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.paddingRight = '';
      window.scrollTo(0, savedY);
    }
  }, []);

  /* ── Close with animation ── */
  const animateClose = useCallback(() => {
    setIsClosing(true);
    setIsEntered(false);
    closeTimer.current = setTimeout(() => {
      setIsClosing(false);
      unlockScroll();
      onClose?.();
    }, drawerDurationMs());
  }, [onClose, unlockScroll]);

  /* ── Mobile swipe-to-close ── */
  const {
    containerRef: mobileDrawerRef,
    dragOffset,
    isDragging,
    DragHandle,
  } = useSwipeToClose(animateClose, isOpen);

  /* ── ESC to close ── */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && isOpen) animateClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, animateClose]);

  /* ── Overlay click ── */
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) animateClose();
  };

  if (!isOpen && !isClosing) return null;

  const drawerStyle = dragOffset > 0 || isDragging
    ? {
        transform: `translateY(${dragOffset}px)`,
        transition: isDragging ? 'none' : 'transform var(--transition-time-drawer) var(--transition-function-drawer)',
      }
    : {};

  const body = (
    <>
      {/* Close button */}
      <div className="info-tooltip__close">
        <Button
          size="tertiary"
          variant="text"
          iconOnly
          iconLeft={<X size={20} />}
          onClick={animateClose}
          aria-label="Close"
        />
      </div>

      <div className="info-tooltip__body">
        {children}
      </div>
    </>
  );

  return (
    <div
      ref={overlayRef}
      className={`info-tooltip__overlay${isClosing ? ' info-tooltip__overlay--closing' : ''}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
    >
      {/* ── Desktop: centered popup ── */}
      <div
        className={`info-tooltip info-tooltip--desktop${isClosing ? ' info-tooltip--closing' : ''}${isEntered ? ' info-tooltip--entered' : ''}`}
      >
        {body}
      </div>

      {/* ── Mobile: bottom sheet drawer ── */}
      <div
        ref={mobileDrawerRef}
        className={`info-tooltip info-tooltip--mobile${isClosing ? ' info-tooltip--closing' : ''}${isEntered ? ' info-tooltip--entered' : ''}`}
        style={drawerStyle}
      >
        <DragHandle className="info-tooltip__swipe-handle" />
        {body}
      </div>
    </div>
  );
};

export default InfoTooltip;
